import React, { useEffect, useRef } from 'react';

const AuroraBg = ({ accentColor = '#c2ffe0', isDark = true, pageBg }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;
    let t = 0;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    resize();

    const hex2rgb = (hex) => {
      const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [194, 255, 224];
    };
    const [ar, ag, ab] = hex2rgb(accentColor);

    // ✅ Use pageBg prop
    const bg = pageBg || (isDark ? '#050510' : '#e8f0ff');

    const draw = () => {
      t += 0.004;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const bands = [
        { y: 0.25, amp: 0.12, speed: 1,   alpha: isDark ? 0.22 : 0.14, color: [ar, ag, ab] },
        { y: 0.40, amp: 0.10, speed: 0.7, alpha: isDark ? 0.16 : 0.10, color: [Math.min(ar + 60, 255), ag, Math.min(ab + 80, 255)] },
        { y: 0.55, amp: 0.14, speed: 1.3, alpha: isDark ? 0.12 : 0.08, color: [ag, ab, ar] },
      ];

      bands.forEach(({ y, amp, speed, alpha, color }) => {
        const H = canvas.height;
        const W = canvas.width;
        const cy = H * y + Math.sin(t * speed) * H * amp;
        const grad = ctx.createLinearGradient(0, cy - H * 0.18, 0, cy + H * 0.18);
        grad.addColorStop(0, `rgba(${color.join(',')},0)`);
        grad.addColorStop(0.5, `rgba(${color.join(',')},${alpha})`);
        grad.addColorStop(1, `rgba(${color.join(',')},0)`);
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(0, cy);
        for (let x = 0; x <= W; x += 4) {
          const wave = Math.sin(x / W * Math.PI * 3 + t * speed * 2) * H * 0.04
            + Math.sin(x / W * Math.PI * 5 - t) * H * 0.02;
          ctx.lineTo(x, cy + wave);
        }
        ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.restore();
      });

      if (isDark) {
        ctx.fillStyle = `rgba(${ar},${ag},${ab},0.7)`;
        for (let i = 0; i < 60; i++) {
          const sx = ((i * 137.5) % canvas.width);
          const sy = ((i * 97.3) % canvas.height);
          const flicker = 0.3 + 0.7 * Math.abs(Math.sin(t * 2 + i));
          ctx.globalAlpha = flicker * 0.5;
          ctx.beginPath();
          ctx.arc(sx, sy, 0.8, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, [accentColor, isDark, pageBg]);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />;
};

export default AuroraBg;